import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Loader2, Plus, Shield } from 'lucide-react';
import { PartnerAccessService } from '@/services/PartnerAccessService';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { OrgDialog } from './org-dialog';

export default function PartnerAccessPage() {
  const [, setLocation] = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['partner-access', 'organisations'],
    queryFn: async () => {
      const response = await PartnerAccessService.listOrganisations();
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to load organisations');
      }
      return response;
    },
  });

  const createMutation = useMutation({
    mutationFn: (input: { key: string; name: string }) => PartnerAccessService.createOrganisation(input),
    onSuccess: (response) => {
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to create organisation');
      }
      queryClient.invalidateQueries({ queryKey: ['partner-access', 'organisations'] });
      setDialogOpen(false);
      setLocation(`/admin/partner-access/${response.data.organisation.id}`);
    },
  });

  const organisations = data?.data?.organisations ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Partner access"
        description="Provision portal organisations, retailer grants, and partner user memberships."
      >
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New organisation
        </Button>
      </PageHeader>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organisation</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Retailer grants</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-red-600">
                  {error instanceof Error ? error.message : 'Failed to load partner organisations.'}
                </TableCell>
              </TableRow>
            ) : organisations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No partner organisations yet.
                </TableCell>
              </TableRow>
            ) : (
              organisations.map((org) => (
                <TableRow
                  key={org.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setLocation(`/admin/partner-access/${org.id}`)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      {org.name}
                    </div>
                  </TableCell>
                  <TableCell>{org.key}</TableCell>
                  <TableCell>{org.memberCount}</TableCell>
                  <TableCell>{org.activeGrantCount}</TableCell>
                  <TableCell>
                    <Badge variant={org.status === 'active' ? 'default' : 'secondary'}>
                      {org.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <OrgDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={(input) => createMutation.mutate(input)}
        isSubmitting={createMutation.isPending}
        error={createMutation.error instanceof Error ? createMutation.error.message : undefined}
      />
    </div>
  );
}
